/**
 * Module 6: do the one action, send from Outlook (3 minutes). The big button from
 * Prepare the reply to Copy the reply and Mark done and next, the reply text in a card
 * above the bar (as primary-action.tsx renders it: reply card, caption, bar), Edit text,
 * the format of a good reply.
 *
 * The reply of PRACTICE-0001 contains "Please find attached our quotation." (facts.ts).
 * Round 3 (12 Oct 2026): Simplified Technical English.
 */

import type { Module } from "@/features/training/types";

import { checkSlide } from "../check-slide";

export const MODULE: Module = {
  id: "reply",
  title: "Do the one action, send from Outlook",
  menuTitle: "Action",
  minutes: 3,
  kind: "lesson",
  goals: [
    "Read the big button: it names the next step and changes as you work.",
    "Copy the reply, check it and send it from Outlook.",
    "Know what a good reply looks like.",
  ],
  helpTopics: ["prepared-replies"],
  slides: [
    {
      id: "r1",
      tone: "light",
      title: "When you need to reply to the customer",
      job: "When you need to reply to the customer, press the big button and paste the reply into Outlook.",
      path: ["Case", "The one action", "Copy the reply"],
      body: [
        {
          kind: "text",
          text: "Step three, **Do the one action**. Under the draft sits one big button. It names the next step and changes as you work: `Confirm`, `Pick 2 items`, `Check`, `Prepare the reply`, `Copy the reply`, `Mark done and next`. `Nothing to do` means exactly that: press it and go to the next case.",
        },
        {
          kind: "list",
          items: [
            "`Prepare the reply`: no reply yet. I2Q writes one from the case.",
            "`Prepare again` with the caption `Made before your changes`: you changed the case after I2Q wrote the reply.",
            "`Copy the reply`, or {key:r}: the subject and the text go to your clipboard. The text sits in a card above the button.",
            "`Mark done and next`: after the copy. It closes the case and opens the next one.",
          ],
        },
        {
          kind: "text",
          text: "A short caption sits above the button and says why it shows this step. When the button says `Copy the reply`, the caption says: **Nothing is sent from here. Copy the reply and send it from Outlook.** `Edit text` and `Undo my edits` sit under `More`.",
        },
      ],
      interaction: {
        id: "r1-panel",
        kind: "hotspots",
        required: true,
        minOpen: 1,
        stage: { view: "case", caseId: "PRACTICE-0001", part: "reply" },
        spots: [
          {
            anchor: "case-reply",
            label: "The reply",
            text: "The subject and the text, in a card above the button. I2Q sends nothing from here.",
          },
        ],
      },
      check: "The reply is a starting text. Read every line before you paste it.",
      labels: [
        "Prepare the reply",
        "Prepare again",
        "Made before your changes",
        "Copy the reply",
        "Mark done and next",
        "Nothing to do",
        "Nothing is sent from here. Copy the reply and send it from Outlook.",
        "Edit text",
        "Undo my edits",
        "More",
      ],
    },
    {
      id: "r2",
      tone: "grey",
      title: "Find what is wrong in this reply",
      job: "Find the 3 sentences that must change before you send.",
      body: [
        {
          kind: "text",
          text: "This is the reply for Example Foundry Ltd. Compare it with the mail and the rows.",
        },
      ],
      interaction: {
        id: "r2-spot",
        kind: "spot",
        required: true,
        mustFind: 3,
        prompt: "Click each sentence that is wrong.",
        rows: [
          {
            id: "r2-thanks",
            text: "Thank you for your inquiry about wear parts for your blast machine.",
            issue: false,
            why: "A plain, correct opening. Keep it.",
          },
          {
            id: "r2-attached",
            text: "Please find attached our quotation.",
            issue: true,
            why: "Nothing is attached. The lines go into the mail itself. Remove this sentence.",
          },
          {
            id: "r2-blade",
            text: "Blade, blast wheel 099-100-01: 8 pcs at 215.00 EUR each, lead time 3 weeks.",
            issue: false,
            why: "Quantity, price and lead time match the case. Keep it.",
          },
          {
            id: "r2-bolt",
            text: "Bolt M12 x 35: 35 pcs.",
            issue: true,
            why: "The customer asked for 200 pcs. 35 is the length of the bolt.",
          },
          {
            id: "r2-liner",
            text: "Liner plate 099-200-07: 1 pc.",
            issue: true,
            why: "The lead time is missing. The item is a guess. Confirm the item. Add the lead time, or write it as an open point.",
          },
          {
            id: "r2-close",
            text: "Please let us know if you have any questions.",
            issue: false,
            why: "A normal closing line. Keep it.",
          },
        ],
      },
      check:
        "Check three things in every reply: no attachment sentence, the right quantities, a lead time for each line.",
    },
    {
      id: "r3",
      tone: "light",
      title: "Correct it and copy it",
      job: "Remove the attachment sentence and copy the reply.",
      path: ["Case", "More", "Edit text"],
      body: [
        {
          kind: "text",
          text: "The big button leads to the reply. When a case does not go through, read the sentence next to the big button and click it. The page goes to the control that is open.",
        },
      ],
      interaction: {
        id: "r3-fix",
        kind: "practice",
        required: true,
        stage: { view: "case", caseId: "PRACTICE-0001", part: "reply" },
        task: "copy-reply-and-fix-text",
        steps: [
          "Open `More` next to the big button and click `Edit text`.",
          "Delete the sentence: Please find attached our quotation.",
          "Open `More` again and click `Done editing`.",
          "Click `Copy the reply`.",
        ],
        success:
          "The reply is on your clipboard with the subject. In Outlook, click Reply on the customer's mail. Paste the text. Check it once more and send it. When every row is decided, the big button says Mark done and next.",
        hints: [
          "`Edit text` sits under `More`, next to the big button. `Undo my edits` appears there after you change the text.",
          "Your changes stay in this browser. I2Q sends nothing.",
        ],
      },
      check: "After you paste into Outlook, read the mail once more. It goes out under your name.",
      labels: ["Edit text", "Done editing", "Copy the reply"],
    },
    {
      id: "r4",
      tone: "grey",
      title: "The format of a good reply",
      job: "Use this format when you write or correct a reply.",
      body: [
        {
          kind: "list",
          items: [
            "Each line in the mail text: quantity, unit price, currency, amount and lead time.",
            "A lead time from the item master reads: about 21 days, to be confirmed. Confirm it before you send the reply.",
            "A line without a quantity in the mail reads: 1 pcs (please confirm the quantity). I2Q quotes 1, and the customer confirms the real quantity.",
            "Open points in a short list at the end.",
            "No sentence about an attached quotation.",
            "Denmark: Danish customers get a Danish draft, German customers a German draft. Every other customer gets English, also customers in Norway and Sweden.",
          ],
        },
        {
          kind: "text",
          text: "Copy a block below when you need it.",
        },
      ],
      interaction: {
        id: "r4-templates",
        kind: "copy",
        required: false,
        templates: [
          {
            id: "r4-open-points",
            title: "Open points block",
            text: "Open points:\n1. Line 2: please confirm the quantity.\n2. Line 3: the lead time will follow.",
          },
          {
            id: "r4-lead-time",
            title: "Lead time to follow",
            text: "We will send you the lead time for this item as soon as we have it.",
          },
          {
            id: "r4-confirm-qty",
            title: "Please confirm the quantity",
            text: "Please confirm the quantity you need for this item. We have quoted 200 pcs.",
          },
        ],
      },
      check: "Every line has a lead time, or it is an open point.",
      labels: ["lead time to confirm", "Open point, line"],
    },
    {
      id: "r5",
      tone: "light",
      title: "Check: before you send",
      job: "Click the right option to finish the module.",
      interaction: {
        id: "r5-check",
        kind: "check",
        required: true,
        question: {
          id: "r5-q",
          topic: "reply",
          stem: "You pasted the reply into Outlook. What do you check?",
          options: [
            {
              id: "r5-q-a",
              text: "Only the subject.",
              correct: false,
              fb: "No. I2Q copies the subject for you. The mistakes hide in the lines.",
            },
            {
              id: "r5-q-b",
              text: "Lines, quantities, unit prices, lead time and open points.",
              correct: true,
              fb: "Right. You send the mail, so you check what it promises.",
            },
            {
              id: "r5-q-c",
              text: "Nothing. I2Q checked it.",
              correct: false,
              fb: "No. The reply is a starting text. I2Q can read a quantity wrong or miss a lead time.",
            },
            {
              id: "r5-q-d",
              text: "Only the greeting.",
              correct: false,
              fb: "No. A friendly greeting does not correct a wrong quantity.",
            },
          ],
        },
      },
      check: "Read the reply in Outlook once more before you click Send.",
    },
    checkSlide({
      id: "r6",
      tone: "grey",
      title: "Check: Copy the reply",
      topic: "reply",
      stem: "What does `Copy the reply` do?",
      answers: [
        ["It sends the reply.", false, "No. Nothing is sent from here."],
        ["It saves a draft in Outlook.", false, "No. It does not touch Outlook."],
        [
          "It puts the text and the subject on your clipboard.",
          true,
          "Right. You paste it into your reply in Outlook.",
        ],
        ["It makes the quotation in F&O.", false, "No. I2Q writes nothing to F&O."],
      ],
      check: "After Copy the reply, you paste, check and send.",
      labels: ["Copy the reply"],
    }),
    checkSlide({
      id: "r7",
      tone: "light",
      title: "Check: missing lead time",
      topic: "reply",
      stem: "Line 3 has no lead time. What goes into the reply?",
      answers: [
        [
          "Nothing. Leave the lead time out.",
          false,
          "No. Customers ask for it next. Say when it follows.",
        ],
        ["A guess of 2 weeks.", false, "No. Promise only a lead time that F&O gives you."],
        [
          "The lead time from F&O, or an open point: the lead time will follow.",
          true,
          "Right. Every line has a lead time or an open point.",
        ],
        ["Remove line 3.", false, "No. The customer asked for it. Keep the line."],
      ],
      check: "No lead time? Write it as an open point.",
    }),
  ],
};
